// IEC bus pin discovery for the setup wizard. Logic only; the dialog is in
// iecwizard_ui.js.
//
// A C64 program (tools/test_prgs/iecfind.bas, mirrored in iecprogram.js) pulses
// DATA 10 times, CLK 7 times and ATN 4 times, one line at a time with quiet gaps
// in between, then keeps a slow CLK heartbeat going. DATA is first because drives
// on the bus answer ATN by pulling DATA low and then hold it: after an ATN phase
// the C64's own DATA pulses cannot move the line any more.
//
// The ESP counts every edge on all free pins (PIN_MODE.EDGES). A burst of edges
// is classified by its busiest pin: ~20 edges = the DATA step, ~14 = the CLK step,
// ~8 = the ATN step. The ATN burst may also show the drives' DATA reaction, so
// ATN is the busiest pin that is not already known as DATA or CLK.
//
// RESET cannot be driven by the C64 software. The user resets the C64 by hand
// (the reset button, or the Ultimate's menu) while the ESP keeps counting: a
// reset pulls the RST line low and releases it, 2 edges, on a pin that is none of
// the known ones. (Pulling RST from the ESP does not reset an Ultimate 64, so the
// ESP does not try.)

import { CANDIDATE_PINS, PIN_MODE } from './device.js';

export const QUIET_END_S = 0.6; // this long without edges ends a burst
export const CLASS_DATA_MIN = 17; // busiest pin of a burst: >= 17 edges = DATA step (20)
export const CLASS_CLK_MIN = 11; //                         11..16 = CLK step (14)
export const CLASS_ATN_MIN = 6; //                           6..10 = ATN step (8); less is noise or heartbeat
const OTHER_PIN_LIMIT = 0.5; // a second pin with this share of the busiest pin's edges is ambiguous
export const RESET_MIN_EDGES = 2; // a reset pulse: low, then released

const pinName = (p) => `GPIO${p}`;

function ranked(totals) {
  return Object.entries(totals)
    .map(([pin, n]) => [Number(pin), n])
    .sort((a, b) => b[1] - a[1] || a[0] - b[0]);
}

export function classify(count) {
  if (count >= CLASS_DATA_MIN) return 'data';
  if (count >= CLASS_CLK_MIN) return 'clk';
  if (count >= CLASS_ATN_MIN) return 'atn';
  return null;
}

// Feed it the polled edge counts with their times; it finds DATA, CLK and ATN.
export class IecSearch {
  constructor() {
    this.reset();
  }

  reset() {
    this.burst = null; // {last, totals}
    this.steps = {}; // class -> {pin, totals}
    this.found = null; // {dat, clk, atn}
    this.problem = null;
  }

  // t in seconds, counts like {16: 5, 33: 1} (empty when nothing moved)
  push(t, counts) {
    if (this.found) return;
    const moved = Object.keys(counts).length > 0;
    if (moved) {
      this.burst ??= { totals: {} };
      for (const [pin, n] of Object.entries(counts)) this.burst.totals[pin] = (this.burst.totals[pin] ?? 0) + n;
      this.burst.last = t;
    } else if (this.burst && t - this.burst.last >= QUIET_END_S) {
      this.#finish(this.burst);
      this.burst = null;
    }
  }

  #finish(burst) {
    const r = ranked(burst.totals);
    const [pin, count] = r[0];
    const cls = classify(count);
    if (!cls) return; // a stray edge, the start-up glitch of the C64, or the heartbeat
    if (cls !== 'atn' && r[1] && r[1][1] >= count * OTHER_PIN_LIMIT) {
      this.problem = `In the ${cls.toUpperCase()} step more than one pin moved (${pinName(r[0][0])} and ${pinName(r[1][0])}). Check the wiring: no other wire may touch the bus lines.`;
      return;
    }
    if (cls !== 'atn') {
      const earlier = this.steps[cls];
      if (earlier && earlier.pin !== pin) {
        this.problem = `The ${cls.toUpperCase()} step was seen on ${pinName(earlier.pin)} and later on ${pinName(pin)}. Run the program again.`;
        return;
      }
    }
    this.steps[cls] = { pin, totals: burst.totals };
    this.problem = null;
    this.#decide();
  }

  #decide() {
    const { data, clk, atn } = this.steps;
    if (!data || !clk || !atn) return;
    if (data.pin === clk.pin) {
      this.problem = `DATA and CLK were seen on the same pin (${pinName(data.pin)}). Check the wiring.`;
      delete this.steps.data;
      delete this.steps.clk;
      return;
    }
    // ATN: the busiest pin of its step that is not DATA or CLK (drives answer ATN on DATA)
    const rest = ranked(atn.totals).filter(([p]) => p !== data.pin && p !== clk.pin);
    if (!rest.length || rest[0][1] < CLASS_ATN_MIN - 1) {
      this.problem = 'The ATN step was not seen on any other pin. Check that the ATN wire is connected.';
      delete this.steps.atn;
      return;
    }
    if (rest[1] && rest[1][1] >= rest[0][1] * OTHER_PIN_LIMIT) {
      this.problem = `In the ATN step more than one unknown pin moved (${pinName(rest[0][0])} and ${pinName(rest[1][0])}). Check the wiring.`;
      delete this.steps.atn;
      return;
    }
    this.found = { dat: data.pin, clk: clk.pin, atn: rest[0][0] };
    this.problem = null;
  }

  // What the user should do while nothing has been found for a while.
  hint(secondsWatching) {
    const have = Object.keys(this.steps);
    if (this.problem) return this.problem;
    if (have.length === 0 && secondsWatching > 12) {
      return 'Nothing recognised yet. Is the cable plugged in at both ends, is the program running, and did it start after this window opened? Reset the C64 and RUN the program again.';
    }
    if (have.includes('clk') && have.includes('atn') && !have.includes('data')) {
      return 'DATA was not seen. Drives on the bus hold DATA low after an earlier run: reset the C64, then RUN the program again. Also check the DATA wire.';
    }
    return null;
  }
}

// Picks the RST pin from the edge totals of the pins that are not otherwise known.
// Returns {pin} or {problem} or null (nothing seen yet).
export function pickReset(totals) {
  const r = ranked(totals).filter(([, n]) => n >= RESET_MIN_EDGES);
  if (!r.length) return null;
  if (r[1] && r[1][1] >= r[0][1] * OTHER_PIN_LIMIT) {
    return { problem: `More than one pin reacted to the reset (${pinName(r[0][0])} and ${pinName(r[1][0])}). Try again; no other wire may touch the bus.` };
  }
  return { pin: r[0][0] };
}

// step: 'prepare' | 'preparing' | 'detect' | 'found' | 'reset' | 'summary' | 'saving' | 'failed'
// automation (optional, ultimate.js IecfindAutomation): an Ultimate that the device controls over Wi-Fi runs the test
// program and resets the C64, so the user does nothing. The device joins the network once and keeps the session for
// the whole search (the radio does not disturb the counting on the DevKit: no pin moved with it on, measured); the
// session is closed when the wizard is cancelled, fails or ends in a restart. During the reset step the pins that
// move before the reset are ignored.
export class IecWizard {
  // clock(): seconds; sleep(ms): resolves later. Both are injectable for tests.
  constructor(device, { onChange = () => {}, clock = () => performance.now() / 1000, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), automation = null } = {}) {
    this.device = device;
    this.automation = automation;
    this.auto = false; // the automated reset step is running
    this.autoStep = null; // what it is doing, for the dialog
    this.onChange = onChange;
    this.clock = clock;
    this.sleep = sleep;
    this.step = 'prepare';
    this.search = new IecSearch();
    this.config = null;
    this.watch = [];
    this.moving = {}; // pins that moved recently, for the live display
    this.pin = { dat: null, clk: null, atn: null, rst: null };
    this.resetCounts = {}; // edges per unknown pin since the reset step began
    this.resetLast = null; // time of the last edge in the reset step
    this.error = null;
    this.hint = null;
    this.testing = false;
    this.startedAt = null;
    this.cancelled = false;
  }

  #set(step, error = null) {
    this.step = step;
    this.error = error;
    this.onChange(this);
  }

  // The user has the cable connected and the program ready: start counting edges.
  async start() {
    this.cancelled = false;
    try {
      if (this.automation) {
        this.autoStep = null;
        this.#set('preparing');
        await this.automation.prepare((t) => {
          this.autoStep = t;
          this.onChange(this);
        });
        if (this.cancelled) return;
      }
      const current = await this.device.getConfig();
      if (!current) throw new Error('could not read the current configuration');
      this.config = current.config;
      // The LED and the button are known and not on the bus: not watched.
      const skip = [this.config.led, this.config.led2, this.config.button].filter((p) => p != null);
      this.watch = CANDIDATE_PINS.filter((p) => !skip.includes(p));
      const status = await this.device.pinTest(PIN_MODE.EDGES, this.watch);
      if (status !== 0) throw new Error(`the device refused to count edges (status ${status})`);
      this.testing = true;
      this.search.reset();
      this.startedAt = this.clock();
      this.#set('detect');
    } catch (e) {
      this.#set('failed', e.message);
    }
  }

  async #read() {
    const { counts } = await this.device.pinCounts();
    const t = this.clock();
    for (const [pin, n] of Object.entries(counts)) if (n > 0) this.moving[pin] = (this.moving[pin] ?? 0) + n;
    return { t, counts };
  }

  // Call about every 250 ms while the step is 'detect'.
  async poll() {
    if (this.step !== 'detect' && this.step !== 'reset') return;
    if (this.auto) return; // the automated reset reads the counts itself
    try {
      const { t, counts } = await this.#read();
      if (this.step === 'reset') return this.#pollReset(t, counts);
      this.search.push(t, counts);
      this.hint = this.search.hint(t - this.startedAt);
      if (this.search.found) {
        Object.assign(this.pin, this.search.found);
        this.#set('found');
      } else {
        this.onChange(this);
      }
    } catch (e) {
      this.#set('failed', e.message);
    }
  }

  // DATA, CLK and ATN are found: now find RESET (or skip it).
  skipReset() {
    this.pin.rst = null;
    this.#set('summary');
    // the test program keeps running on the C64: with an Ultimate to control, stop it with a reset
    this.automation?.open().then(() => this.automation.reset()).catch(() => this.automation.close().catch(() => {}));
  }

  // The user is asked to reset the C64 by hand.
  waitForReset() {
    if (this.automation) return this.#autoReset();
    this.resetCounts = {};
    this.resetLast = null;
    this.hint = null;
    this.#set('reset');
  }

  // The Ultimate resets the C64 and the pin that reacts is RESET. Pins that move in a quiet moment before the reset
  // (noise, or another board's radio) are not candidates. Up to three attempts.
  async #autoReset() {
    this.auto = true;
    this.hint = null;
    this.resetCounts = {};
    this.resetLast = null;
    const say = (t) => {
      this.autoStep = t;
      this.onChange(this);
    };
    this.autoStep = null;
    this.#set('reset');
    const known = new Set([this.pin.dat, this.pin.clk, this.pin.atn]);
    try {
      for (let attempt = 0; attempt < 3 && !this.cancelled; attempt++) {
        await this.#read(); // forget what was counted so far
        await this.automation.open(); // the session of the test program is still open (it joins again if it was lost)
        await this.sleep(1500);
        const noisy = new Set(Object.keys((await this.#read()).counts).map(Number)); // moved with nothing happening
        say('Resetting the C64');
        await this.automation.reset();
        await this.sleep(2500);
        const during = (await this.#read()).counts;
        const totals = {};
        for (const [pin, n] of Object.entries(during)) {
          if (known.has(Number(pin)) || noisy.has(Number(pin))) continue;
          totals[pin] = (totals[pin] ?? 0) + n;
        }
        const pick = pickReset(totals);
        if (pick?.pin != null) {
          this.pin.rst = pick.pin;
          this.hint = null;
          this.auto = false;
          return this.#set('summary');
        }
        this.hint = pick?.problem ?? 'No pin reacted to the reset. Is the RESET wire connected?';
      }
      this.auto = false;
      this.autoStep = null;
      this.onChange(this); // out of attempts: the dialog offers "No RESET wire" and "Try again"
    } catch (e) {
      this.auto = false;
      this.automation.close().catch(() => {});
      this.#set('failed', e.message);
    }
  }

  #pollReset(t, counts) {
    const known = new Set([this.pin.dat, this.pin.clk, this.pin.atn]);
    let moved = false;
    for (const [pin, n] of Object.entries(counts)) {
      if (known.has(Number(pin))) continue;
      this.resetCounts[pin] = (this.resetCounts[pin] ?? 0) + n;
      moved = true;
    }
    if (moved) this.resetLast = t;
    const pick = this.resetLast != null && t - this.resetLast >= QUIET_END_S ? pickReset(this.resetCounts) : null;
    if (pick?.pin != null) {
      this.pin.rst = pick.pin;
      this.hint = null;
      return this.#set('summary');
    }
    if (pick?.problem) {
      this.hint = pick.problem;
      this.resetCounts = {};
      this.resetLast = null;
    }
    this.onChange(this);
  }

  async save() {
    if (this.step !== 'summary') throw new Error('nothing to save yet');
    this.#set('saving');
    try {
      const current = await this.device.getConfig();
      if (!current) throw new Error('could not read the current configuration');
      const status = await this.device.setConfig({
        ...current.config,
        dat: this.pin.dat, clk: this.pin.clk, atn: this.pin.atn, rst: this.pin.rst,
      });
      if (status !== 0) throw new Error(`the device refused the configuration (status ${status})`);
      this.testing = false; // the restart ends the test
    } catch (e) {
      this.#set('failed', e.message);
      throw e;
    }
  }

  // Back to counting from scratch (the program has to be started again).
  async retry() {
    this.cancelled = true;
    this.search.reset();
    this.pin = { dat: null, clk: null, atn: null, rst: null };
    this.moving = {};
    this.resetCounts = {};
    this.resetLast = null;
    this.startedAt = this.clock();
    if (!this.testing || this.automation) return this.start(); // with an Ultimate the test program is started again too
    this.#set('detect');
  }

  // Leaves the wizard without saving: ends a running test (this restarts the ESP).
  async cancel() {
    this.cancelled = true;
    this.automation?.close().catch(() => {}); // the radio must not stay on
    if (!this.testing) return;
    this.testing = false;
    try {
      await this.device.pinTest(PIN_MODE.STOP);
    } catch {
      // the ESP restarts and may not answer
    }
  }
}
